import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import IconAdd from '@mui/icons-material/Add';
import IconEdit from '@mui/icons-material/Edit';
import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';
import styles from './thermography.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import ModulePdfButton from '../moduleCard/ModulePdfButton';
import DateChip from '../moduleCard/DateChip';
import ThermographyPopup from '../../../../popup/ThermographyPopup/ThermographyPopup';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import ModuleReportTemplate from '../../../../components/documents-templates/report/ModuleReportTemplate';
import ThermographyTable from './ThermographyTable';
import { MODULE_KEYS } from '../../modules/appointmentModules';
import { REPORT_SECTIONS, buildThermographyJob } from '../../modules/reportSections';
import { findOwnRecord, formatDate, formatTemp, formatThreshold, sortByDateDesc } from '../../modules/thermography/thermography';

const SECTION = REPORT_SECTIONS[MODULE_KEYS.THERMOGRAPHY];

/**
 * Módulo "Termografia": temperatura da pele por local do corpo, com delta T
 * entre os lados, destaque de assimetria acima do limiar e comparação com a
 * avaliação anterior. Uma avaliação por atendimento.
 *
 * `data`: avaliações do paciente (todas, de qualquer atendimento).
 * `enabled`: false na aba Saúde do paciente (só leitura, mas ainda exporta).
 */
const Thermography = ({
  data,
  patientId,
  appointmentId,
  appointmentDate,
  enabled,
  collapsible = false,
  onUpdated,
  patientName,
  professionalId,
  companyConfig,
}) => {
  const [history, setHistory] = useState(Array.isArray(data) ? data : []);
  const [selectedId, setSelectedId] = useState(null);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    setHistory(Array.isArray(data) ? data : []);
  }, [data]);

  const sorted = useMemo(() => sortByDateDesc(history), [history]);
  const ownEntry = findOwnRecord(history, appointmentId);
  const selected = (selectedId != null && sorted.find((item) => String(item.id) === String(selectedId)))
    || ownEntry
    || sorted[0]
    || null;
  const job = useMemo(() => buildThermographyJob(history, selected), [history, selected]);

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: SECTION.reportType,
    prepare: () => buildThermographyJob(history, selected),
  });

  function popupHideHandler(needsReload) {
    setPopup(null);
    if (needsReload && onUpdated) onUpdated();
  }

  const actions = (
    <>
      {job && <ModulePdfButton report={report} />}
      {enabled && (ownEntry ? (
        <CustomButton
          variant="outlined"
          style="primary"
          label="Editar avaliação"
          icon={<IconEdit />}
          onClick={() => setPopup({ entry: ownEntry })}
        />
      ) : (
        <CustomButton
          variant="contained"
          style="primary"
          label="Nova avaliação"
          icon={<IconAdd />}
          onClick={() => setPopup({ entry: null })}
        />
      ))}
    </>
  );

  return (
    <>
      <ModuleCard
        accent="amber"
        title="Termografia"
        icon={<DeviceThermostatOutlinedIcon />}
        description="Temperatura da pele por local, com diferença entre os lados"
        actions={actions}
        collapsible={collapsible}
      >
        <div className={styles.content}>
          {!job ? (
            <ModuleEmptyState icon={<DeviceThermostatOutlinedIcon />} text="Nenhuma avaliação termográfica registrada." />
          ) : (
            <>
              <div className={styles.toolbar}>
                <div className={styles.context}>
                  <DateChip label={formatDate(job.record.measuredAt)} />
                  {job.record.ambientTemp != null && (
                    <span className={parts.chip}>Sala: {formatTemp(job.record.ambientTemp)} °C</span>
                  )}
                  <span className={parts.chip}>{job.sites} {job.sites === 1 ? 'local' : 'locais'}</span>
                  <span className={`${parts.chip} ${job.asymmetries > 0 ? styles.chipAlert : styles.chipOk}`}>
                    {job.asymmetries === 0
                      ? 'Sem assimetria'
                      : `${job.asymmetries} ${job.asymmetries === 1 ? 'assimetria' : 'assimetrias'}`}
                  </span>
                  <span className={parts.muted}>
                    {job.previous ? `Comparado com ${formatDate(job.previous.measuredAt)}` : 'Primeira avaliação'}
                  </span>
                </div>

                {sorted.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="thermography-record-label">Avaliação</InputLabel>
                    <Select
                      labelId="thermography-record-label"
                      label="Avaliação"
                      value={String(job.record.id)}
                      onChange={(e) => setSelectedId(e.target.value)}
                    >
                      {sorted.map((item) => (
                        <MenuItem key={item.id} value={String(item.id)}>{formatDate(item.measuredAt)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </div>

              <ThermographyTable groups={job.groups} showPrevious={!!job.previous} />

              {job.record.notes && <div className={styles.notes}>{job.record.notes}</div>}

              <span className={styles.footnote}>
                ΔT = direito menos esquerdo. Assimetria destacada quando a diferença entre os lados passa de {formatThreshold()} °C.
              </span>
            </>
          )}
        </div>
      </ModuleCard>

      {report.job && (
        <HiddenReport>
          <ModuleReportTemplate ref={report.ref} section={SECTION} job={report.job} {...report.paperProps} />
        </HiddenReport>
      )}

      {popup && (
        <ThermographyPopup
          patientId={patientId}
          appointmentId={appointmentId}
          appointmentDate={appointmentDate}
          entry={popup.entry}
          history={sorted}
          OnHide={popupHideHandler}
        />
      )}
    </>
  );
};

export default Thermography;
